/**
 * Quarantine Review Card Component
 * TASK-361: Document Management & Compliance System
 *
 * Card for reviewing and accepting/rejecting documents uploaded
 * through the public upload portal.
 */

import React, { useState } from 'react';
import {
  FileText,
  Image,
  Table,
  File,
  Eye,
  Check,
  X,
  AlertTriangle,
  Clock,
  User,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  QuarantineDocument,
  DocType,
  getDocTypeLabel,
  getQuarantineStatusColor,
  getQuarantineStatusLabel,
} from '../../types/documentCompliance';
import { formatFileSize } from '../../utils/formatters';

interface QuarantineReviewCardProps {
  document: QuarantineDocument;
  onAccept: (docId: string, docType: DocType, expiresAt?: string) => Promise<void>;
  onReject: (docId: string, reason: string, allowReupload?: boolean) => Promise<void>;
  onPreview: (docId: string) => void;
}

const REJECTION_REASONS = [
  'Document is illegible or poor quality',
  'Wrong document type uploaded',
  'Document is expired',
  'Document is incomplete',
  'Sensitive information exposed',
  'Other (please specify)',
];

const QuarantineReviewCard: React.FC<QuarantineReviewCardProps> = ({
  document,
  onAccept,
  onReject,
  onPreview,
}) => {
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Accept form state
  const [selectedDocType, setSelectedDocType] = useState<DocType>(document.doc_type);
  const [expiresAt, setExpiresAt] = useState('');
  const [_description, _setDescription] = useState('');

  // Reject form state
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [allowReupload, setAllowReupload] = useState(true);

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <File className="w-8 h-8 text-gray-400" />;
    if (mimeType.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (mimeType.includes('image')) return <Image className="w-8 h-8 text-blue-500" />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
      return <Table className="w-8 h-8 text-green-500" />;
    return <File className="w-8 h-8 text-gray-400" />;
  };

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      await onAccept(document.id, selectedDocType, expiresAt || undefined);
      setShowAcceptForm(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = rejectionReason === 'Other (please specify)'
      ? customReason
      : rejectionReason;

    if (!reason.trim()) return;

    setIsLoading(true);
    try {
      await onReject(document.id, reason, allowReupload);
      setShowRejectForm(false);
    } finally {
      setIsLoading(false);
    }
  };

  const isPending = document.quarantine_status === 'pending' || document.quarantine_status === 'clean';
  const isInfected = document.quarantine_status === 'infected';

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Card Header */}
      <div className="flex items-start justify-between p-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          {getFileIcon(document.mime_type)}
          <div>
            <h4 className="font-medium text-gray-900 text-sm">
              {document.original_filename}
            </h4>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span>{document.file_size_formatted || formatFileSize(document.file_size)}</span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {new Date(document.uploaded_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getQuarantineStatusColor(document.quarantine_status)}`}>
          {getQuarantineStatusLabel(document.quarantine_status)}
        </span>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* Client and Doc Type Info */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <User size={14} />
            <span>{document.client_name || 'Unknown Client'}</span>
          </div>
          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
            {getDocTypeLabel(document.doc_type)}
          </span>
        </div>

        {/* Security Warning for Infected */}
        {isInfected && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertTriangle size={16} />
            <span>Security scan detected potential issues with this file.</span>
          </div>
        )}

        {/* Action Buttons */}
        {isPending && !showAcceptForm && !showRejectForm && (
          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreview(document.id)}
              className="flex-1 flex items-center justify-center gap-1.5"
            >
              <Eye size={14} />
              Preview
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRejectForm(true)}
              className="flex-1 flex items-center justify-center gap-1.5 text-red-600 hover:bg-red-50"
            >
              <X size={14} />
              Reject
            </Button>
            <Button
              size="sm"
              onClick={() => setShowAcceptForm(true)}
              className="flex-1 flex items-center justify-center gap-1.5"
            >
              <Check size={14} />
              Accept
            </Button>
          </div>
        )}

        {/* Accept Form */}
        {showAcceptForm && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <h5 className="font-medium text-sm text-gray-900">Accept Document</h5>

            {/* Document Type Selection */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Document Category
              </label>
              <select
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value as DocType)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              >
                {(['kyc', 'portfolio', 'risk-profile', 'aml', 'compliance', 'contracts', 'general'] as DocType[]).map((type) => (
                  <option key={type} value={type}>
                    {getDocTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Expiration Date <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAcceptForm(false)}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-1.5"
              >
                {isLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Confirm Accept
              </Button>
            </div>
          </div>
        )}

        {/* Reject Form */}
        {showRejectForm && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <h5 className="font-medium text-sm text-gray-900">Reject Document</h5>

            {/* Rejection Reason */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reason for Rejection
              </label>
              <select
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
              >
                <option value="">Select a reason...</option>
                {REJECTION_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Reason */}
            {rejectionReason === 'Other (please specify)' && (
              <div>
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Please specify the reason..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500] resize-none"
                />
              </div>
            )}

            {/* Allow Re-upload Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allowReupload}
                onChange={(e) => setAllowReupload(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
              />
              <span className="text-sm text-gray-700">
                Allow client to re-upload
              </span>
            </label>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRejectForm(false)}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleReject}
                disabled={isLoading || !rejectionReason || (rejectionReason === 'Other (please specify)' && !customReason.trim())}
                className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700"
              >
                {isLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <X size={14} />
                )}
                Confirm Reject
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuarantineReviewCard;
